import React, { useState } from 'react';
import { X, Check, Info, Database, Users, ShoppingCart, CreditCard, Ticket, Star } from 'lucide-react';

const TableSelectionModal = ({ 
  isOpen, 
  onClose, 
  suggestedTables, 
  onTablesSelected,
  title = "Select Tables",
  message = "Choose the tables that contain the data you need:",
  icon = "📋"
}) => {
  const [selectedTables, setSelectedTables] = useState([]);

  if (!isOpen) return null;

  const handleTableToggle = (tableName) => {
    setSelectedTables(prev => 
      prev.includes(tableName) 
        ? prev.filter(t => t !== tableName)
        : [...prev, tableName]
    );
  };

  const handleConfirm = () => {
    if (selectedTables.length > 0) {
      onTablesSelected(selectedTables);
      onClose();
    }
  };

  const getTableIcon = (tableName) => {
    const name = tableName.toLowerCase();
    if (name.includes('user') || name.includes('profile')) return <Users className="w-5 h-5" />;
    if (name.includes('order') || name.includes('purchase')) return <ShoppingCart className="w-5 h-5" />;
    if (name.includes('wallet') || name.includes('transaction')) return <CreditCard className="w-5 h-5" />;
    if (name.includes('ticket') || name.includes('event')) return <Ticket className="w-5 h-5" />;
    if (name.includes('rating') || name.includes('review')) return <Star className="w-5 h-5" />;
    return <Database className="w-5 h-5" />;
  };

  const getRelevanceColor = (score) => {
    if (score >= 3) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 2) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{icon}</span>
              <div>
                <h2 className="text-xl font-bold">{title}</h2>
                <p className="text-violet-100">{message}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Table Selection */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid gap-4">
            {suggestedTables.map((table) => (
              <div
                key={table.table_name}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedTables.includes(table.table_name)
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleTableToggle(table.table_name)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full">
                      {getTableIcon(table.table_name)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-medium text-gray-900">{table.table_name}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full border ${getRelevanceColor(table.relevance_score)}`}>
                          Score: {table.relevance_score.toFixed(1)}
                        </span>
                        {table.is_core_table && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full border border-blue-200">
                            Core Table
                          </span>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{table.description}</p>
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>{table.row_count.toLocaleString()} rows</span>
                        {table.key_columns.length > 0 && (
                          <span>Key columns: {table.key_columns.slice(0, 3).join(', ')}</span>
                        )}
                      </div>
                      
                      {table.match_reasons.length > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center space-x-1 text-xs text-gray-500 mb-1">
                            <Info className="w-3 h-3" />
                            <span>Why this table:</span>
                          </div>
                          <ul className="text-xs text-gray-600 space-y-1">
                            {table.match_reasons.slice(0, 3).map((reason, index) => (
                              <li key={index} className="flex items-center space-x-1">
                                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                                <span>{reason}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {table.relationships.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">
                            Related tables: {table.relationships.join(', ')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedTables.includes(table.table_name)
                        ? 'bg-violet-500 border-violet-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedTables.includes(table.table_name) && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedTables.length > 0 ? (
                <span>
                  Selected {selectedTables.length} table{selectedTables.length !== 1 ? 's' : ''}: {' '}
                  <span className="font-medium">{selectedTables.join(', ')}</span>
                </span>
              ) : (
                <span>Select one or more tables to generate a focused query</span>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedTables.length === 0}
                className={`px-6 py-2 rounded-lg font-medium ${
                  selectedTables.length > 0
                    ? 'bg-violet-600 text-white hover:bg-violet-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Generate Query
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableSelectionModal;